import { createFileRoute } from "@tanstack/react-router";
import { Cable, Workflow, Zap } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/automations-hero.webp";
import canvasImg from "@/assets/automations-canvas.webp";
import reliabilityImg from "@/assets/automations-reliability.webp";

const IMG: Record<string, string> = { hero: heroImg, canvas: canvasImg, reliability: reliabilityImg };

export const Route = createFileRoute("/vi/platform/automations")({
  head: () => ({
    meta: [
      { title: "Tự động hóa — banana.cards" },
      { name: "description", content: "Trigger, action, điều kiện và chờ — automation trực quan kết nối mọi phần doanh nghiệp." },
      { property: "og:title", content: "Tự động hóa — banana.cards" },
      { property: "og:description", content: "Trigger, action, điều kiện và chờ — automation trực quan kết nối mọi phần doanh nghiệp." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/automations", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Tự động hóa"
      title="Nếu cái này,"
      italic="thì mọi thứ."
      description="Kích hoạt workflow khi thẻ được chạm, form được gửi, hoặc deal đổi giai đoạn. Nối chuỗi action giữa Banana và 50+ công cụ bên ngoài."
      heroImage={IMG['hero']}
      metrics={[{ v: "100+", l: "trigger" }, { v: "50+", l: "tích hợp" }, { v: "0", l: "dòng code" }]}
      bullets={[
        { icon: Zap, title: "Trigger có sẵn", desc: "Từ mọi sự kiện Banana — chạm, form, giai đoạn deal, trạng thái hợp đồng." },
        { icon: Workflow, title: "Action mạnh mẽ", desc: "Tag, gán, gửi email, thông báo, tính phí, đồng bộ — tất cả trên một canvas." },
        { icon: Cable, title: "Công cụ bên ngoài", desc: "Kết nối Slack, Notion, Sheets, Zapier, n8n và 50+ công cụ khác." }
      ]}
      deepDives={[
        { eyebrow: "Canvas", title: "Trình tạo trực quan", italic: "với điều kiện.", description: "Kéo thả trigger và action, thêm nhánh và bước chờ — xem trước mỗi lần chạy trước khi triển khai.", image: IMG["canvas"], reverse: false, points: ["Phân nhánh theo điều kiện", "Bước chờ và delay", "Chế độ test với dữ liệu mẫu", "Phiên bản và rollback"] },
        { eyebrow: "Độ tin cậy", title: "Không bao giờ", italic: "rớt một job.", description: "Mọi lần chạy được log với retry, xử lý dead-letter và quan sát từng bước.", image: IMG["reliability"], reverse: true, points: ["Đảm bảo gửi ít nhất một lần", "Retry với backoff", "Lịch sử chạy với payload", "Cảnh báo lỗi theo bước"] }
      ]}
      howItWorks={[
        { title: "Chọn trigger", desc: "Từ bất kỳ sự kiện Banana nào." },
        { title: "Thêm action", desc: "Công cụ nội bộ hoặc bên ngoài." },
        { title: "Thêm điều kiện", desc: "Phân nhánh đường đi." },
        { title: "Triển khai & theo dõi", desc: "Có log và cảnh báo." }
      ]}
      capabilities={["100+ trigger và action", "Phân nhánh điều kiện", "Bước chờ & delay", "Log chạy với payload", "Phiên bản và rollback", "Cảnh báo lỗi theo bước", "Tích hợp native + Zapier", "Webhook vào và ra"]}
      faq={[
        { q: "Tôi có thể tự động hóa gì trong Banana?", a: "Mọi thứ nối hai phần workspace: 'khi có lead mới có tag Lead, gửi email chào và tạo task follow-up sau 2 ngày', 'khi deal chuyển sang Won, tạo hợp đồng onboarding và lời mời họp kick-off'." },
        { q: "Có cần biết code không?", a: "Không — automation được tạo trực quan bằng cách chọn trigger và action từ dropdown. Người dùng nâng cao có thể dùng action JSON, nhưng đó là tùy chọn." },
        { q: "Có thể test automation trước khi bật không?", a: "Có — mỗi automation có chế độ dry-run mô phỏng trigger và hiển thị chính xác action nào sẽ chạy với payload nào." },
        { q: "Nếu một bước thất bại thì sao?", a: "Banana retry với exponential backoff (3 lần), sau đó hiển thị lỗi trong lịch sử automation với payload đầy đủ để debug." }
      ]}
      outro={{ title: "Tự động hóa việc nhàm chán.", italic: "Giữ lại mối quan hệ.", description: "Workflow đẩy deal tiến lên trong khi bạn tập trung vào con người." }}
    />
  );
}